import React from "react";
import PropTypes from "prop-types";
import { Card, Link, Container, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { RED } from "assets/theme/brandColors";

const Section = ({ title, children }) => (
  <MDBox mb={2}>
    <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
      {title}
    </MDTypography>
    <MDTypography variant="body2" color="text">
      {children}
    </MDTypography>
  </MDBox>
);

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function TermsAndConditions() {
  const navigate = useNavigate();
  return (
    <MDBox
      sx={{
        background: `linear-gradient(180deg, ${RED} 20%, #fe8361 80%)`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        py: 3,
      }}
    >
      <Container maxWidth="md">
        <Card
          sx={{
            p: 4,
            backgroundColor: "#fff",
            mx: "auto",
            transform: "scale(0.85)",
            transformOrigin: "top center",
          }}
        >
          <MDBox mb={1} textAlign="center">
            <MDTypography variant="h3" fontWeight="bold" sx={{ color: RED }}>
              Terms of Service
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Last updated: April 2026
            </MDTypography>
          </MDBox>

          <MDBox
            mb={3}
            p={2}
            sx={{ background: "#faf8f5", borderRadius: 2, borderLeft: `3px solid ${RED}` }}
          >
            <MDTypography variant="body2" color="text">
              <strong>Summary:</strong> Estìa is a free beta research project. Content may be
              AI-generated and unverified. You use this service entirely at your own risk. Estìa
              accepts no liability for any outcome arising from use of this platform.
            </MDTypography>
          </MDBox>

          <Section title="1. About Estìa">
            Estìa is a free, early-stage research project currently in beta. References to
            &quot;Estìa&quot;, &quot;we&quot;, or &quot;us&quot; refer to the Estìa project. By
            creating an account you acknowledge that you are participating in a research and
            development phase, that the platform is experimental, and that features may change,
            break, or be removed without notice.
          </Section>

          <Section title="2. No warranty — service provided as-is">
            Estìa is provided &quot;as is&quot; and &quot;as available&quot; without any warranty of
            any kind, express or implied, including but not limited to warranties of
            merchantability, fitness for a particular purpose, or non-infringement. We do not
            warrant that the service will be uninterrupted, error-free, or free of harmful
            components. Your use of Estìa is entirely at your own risk.
          </Section>

          <Section title="3. AI-generated and unverified content">
            Recipes, nutritional information, ingredient data, and other content on Estìa may be
            generated or curated using artificial intelligence. This content is not reviewed or
            verified by a qualified chef, nutritionist, dietitian, or any other professional. It may
            contain errors, inaccuracies, or omissions. You must exercise your own independent
            judgement before acting on any content found on this platform. Estìa makes no
            representation that any content is accurate, complete, safe, or suitable for your
            individual circumstances.
          </Section>

          <Section title="4. Kitchen safety and personal responsibility">
            Cooking involves inherent risks including but not limited to burns, cuts, fire,
            foodborne illness, and allergic reactions. By using Estìa you agree that you are solely
            responsible for your actions in the kitchen. Estìa accepts no liability whatsoever for
            any injury, illness, property damage, or other harm arising from your use of recipes,
            techniques, or guidance found on this platform, whether such content was accurate or
            not.
          </Section>

          <Section title="5. Allergens and dietary requirements">
            Recipe content on Estìa may include or omit allergen information and may not reflect
            your personal dietary needs or restrictions. Allergen information on Estìa may be
            AI-generated and has not been verified by a food safety professional. If you have any
            known food allergies, intolerances, health conditions, or dietary requirements, you must
            independently verify all ingredients before cooking. Always consult a qualified
            healthcare professional or registered dietitian for personalised dietary advice. Estìa
            is not responsible for any allergic reaction or adverse health outcome.
          </Section>

          <Section title="6. Not medical or nutritional advice">
            Nothing on Estìa constitutes medical, nutritional, or health advice. Content is provided
            for general informational and entertainment purposes only. Always consult a qualified
            healthcare provider before making changes to your diet, especially if you have a medical
            condition.
          </Section>

          <Section title="7. Voluntary donations">
            Estìa may offer an option to make a voluntary financial contribution to support
            development. Any such contribution is a donation, not a payment for goods or services.
            Donations confer no entitlement to any feature, level of service, or continued access.
            All donations are final and non-refundable.
          </Section>

          <Section title="8. Future paid plans">
            Estìa is currently free. At such time as a paid tier is introduced, updated terms will
            be presented to users before any charge is made. A registered legal entity will be
            established prior to any commercial offering. Continued use of a free tier will not
            automatically enrol you in any paid plan.
          </Section>

          <Section title="9. Limitation of liability">
            To the fullest extent permitted by applicable law, Estìa and its operators shall not be
            liable for any direct, indirect, incidental, special, consequential, or punitive damages
            arising out of or in connection with your use of, or inability to use, this platform or
            its content — including but not limited to personal injury, property damage, data loss,
            or financial loss — even if advised of the possibility of such damages.
          </Section>

          <Section title="10. Intellectual property">
            All content on Estìa, including recipes, text, audio, and design, is the property of
            Estìa unless otherwise stated. You may not copy, reproduce, or distribute any content
            without prior written permission.
          </Section>

          <Section title="11. Changes to these terms">
            These terms may be updated at any time. Continued use of the platform after changes are
            posted constitutes acceptance of the revised terms. The date at the top of this page
            reflects the most recent update.
          </Section>

          <Section title="12. Contact">
            For any questions about these terms:{" "}
            <Link href="mailto:estiahelp@outlook.com" underline="hover" sx={{ color: RED }}>
              estiahelp@outlook.com
            </Link>
          </Section>

          <MDBox textAlign="center" mt={3} mb={1}>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#fe8361" }}>
              Happy Cooking!
            </MDTypography>
          </MDBox>

          <MDBox textAlign="center" mt={2} mb={1}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: RED,
                "&:hover": { backgroundColor: "#fe8361" },
                fontWeight: "bold",
                color: "#fff",
                px: 6,
                py: 1.5,
                fontSize: "1rem",
              }}
              onClick={() => navigate("/authentication/sign-up")}
            >
              Back to Sign Up
            </Button>
          </MDBox>
        </Card>
      </Container>
    </MDBox>
  );
}

export default TermsAndConditions;
